/* eslint-disable react/jsx-no-bind */
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.any';
import { isInterpreter, isClient } from '../../../base/user-role/functions';
import Switch from '../../../base/ui/components/web/Switch';
import { queueService } from '../../../interpreter-queue/InterpreterQueueService';
import { getDefaultInterpreterLanguages } from '../../../interpreter-queue/interpreterLanguages';
import InterpreterRequestPopup from '../../../interpreter-queue/components/web/InterpreterRequestPopup';
import { offerTiming } from '../../../interpreter-queue/offerTiming';
import { resolveQueueRequest } from '../../../interpreter-queue/resolveQueueRequest';
import MinimizedRequestList from '../../../interpreter-queue/components/web/MinimizedRequestList';
import { prepareInterpreterOfferAlerts } from '../../../interpreter-queue/interpreterOfferAlert.web';
import { getPersistentJson } from '../../../vrs-auth/storage';
import { ContactsDrawer } from '../../../contacts';
import Avatar from '../../../base/avatar/components/Avatar';
import { isNameReadOnly } from '../../../base/config/functions.web';
import { IconArrowDown, IconArrowUp, IconCopy, IconPhoneRinging, IconUsers, IconVolumeOff } from '../../../base/icons/svg';
import { isVideoMutedByUser } from '../../../base/media/functions';
import { getLocalParticipant } from '../../../base/participants/functions';
import Popover from '../../../base/popover/components/Popover.web';
import ActionButton from '../../../base/premeeting/components/web/ActionButton';
import PreMeetingScreen from '../../../base/premeeting/components/web/PreMeetingScreen';
import { updateSettings } from '../../../base/settings/actions';
import { getDisplayName } from '../../../base/settings/functions.web';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import { getLocalJitsiVideoTrack } from '../../../base/tracks/functions.web';
import Input from '../../../base/ui/components/web/Input';
import isInsecureRoomName from '../../../base/util/isInsecureRoomName';
import { openDisplayNamePrompt } from '../../../display-name/actions';
import { isUnsafeRoomWarningEnabled } from '../../../prejoin/functions';
import { joinConference as joinConferenceAction, joinConferenceWithoutAudio as joinConferenceWithoutAudioAction, setJoinByPhoneDialogVisiblity as setJoinByPhoneDialogVisiblityAction } from '../../actions.web';
import { isDeviceStatusVisible, isDisplayNameRequired, isJoinByPhoneButtonVisible, isJoinByPhoneDialogVisible, isPrejoinDisplayNameVisible } from '../../functions';
import { hasDisplayName } from '../../utils';
import JoinByPhoneDialog from './dialogs/JoinByPhoneDialog';
function getApiBase() {
    if (typeof config !== 'undefined' && config.vrs?.queueServiceUrl) {
        const wsUrl = config.vrs.queueServiceUrl;
        return wsUrl.replace(/^ws/, 'http').replace(/\/ws$/, '');
    }
    return typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001';
}
function getCurrentRoomName() {
    if (typeof window === 'undefined') {
        return '';
    }
    const path = window.location.pathname.split('/').filter(Boolean);
    const roomName = path[path.length - 1];
    if (!roomName || roomName.endsWith('.html')) {
        return '';
    }
    return roomName;
}
function getPreferredClientName() {
    const storedUser = getPersistentJson('vrs_user_info');
    return storedUser?.name?.trim() || '';
}
function getInterpreterProfileLanguages() {
    const storedUser = getPersistentJson('vrs_user_info');
    return Array.isArray(storedUser?.languages) && storedUser.languages.length
        ? storedUser.languages
        : getDefaultInterpreterLanguages();
}
function getFirstName(name) {
    return (name || '').trim().split(/\s+/)[0] || '';
}
const useStyles = makeStyles()(theme => {
    return {
        inputContainer: {
            width: '100%'
        },
        input: {
            width: '100%',
            marginBottom: theme.spacing(3),
            '& input': {
                textAlign: 'center'
            }
        },
        avatarContainer: {
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column'
        },
        avatar: {
            margin: `${theme.spacing(2)} auto ${theme.spacing(3)}`
        },
        avatarName: {
            ...withPixelLineHeight(theme.typography.bodyShortBoldLarge),
            color: theme.palette.text01,
            marginBottom: theme.spacing(3),
            textAlign: 'center'
        },
        interpreterButton: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(1),
            minHeight: '48px',
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.success02,
            color: theme.palette.text01,
            fontSize: '14px',
            fontWeight: 600,
            transition: 'all 0.2s ease-in-out',
            border: 'none',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: theme.palette.success01,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
            },
            '&:active': {
                transform: 'translateY(0)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }
        },
        interpreterToggleContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(1),
            padding: theme.spacing(2),
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.ui02,
            border: `1px solid ${theme.palette.ui03}`
        },
        interpreterToggleLabel: {
            ...withPixelLineHeight(theme.typography.bodyShortRegular),
            color: theme.palette.text01,
            marginRight: theme.spacing(2),
            fontWeight: 500
        },
        interpreterToggleWrapper: {
            display: 'flex',
            alignItems: 'center'
        },
        interpreterToggleStatus: {
            ...withPixelLineHeight(theme.typography.labelBold),
            marginLeft: theme.spacing(2),
            minWidth: '60px',
            textAlign: 'center'
        },
        activeStatus: {
            color: theme.palette.success01
        },
        inactiveStatus: {
            color: theme.palette.text03
        },
        error: {
            backgroundColor: theme.palette.actionDanger,
            color: theme.palette.text01,
            borderRadius: theme.shape.borderRadius,
            width: '100%',
            ...withPixelLineHeight(theme.typography.labelRegular),
            boxSizing: 'border-box',
            padding: theme.spacing(1),
            textAlign: 'center',
            marginTop: `-${theme.spacing(2)}`,
            marginBottom: theme.spacing(3)
        },
        dropdownContainer: {
            position: 'relative',
            width: '100%'
        },
        dropdownButtons: {
            width: '300px',
            padding: '8px 0',
            backgroundColor: theme.palette.action02,
            color: theme.palette.text04,
            borderRadius: theme.shape.borderRadius,
            position: 'relative',
            top: `-${theme.spacing(3)}`,
            '@media (max-width: 511px)': {
                margin: '0 auto',
                top: 0
            },
            '@media (max-width: 420px)': {
                top: 0,
                width: 'calc(100% - 32px)'
            }
        },
        contactsPanel: {
            marginTop: theme.spacing(2),
            padding: theme.spacing(2),
            borderRadius: theme.shape.borderRadius,
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            border: `1px solid ${theme.palette.ui03}`,
            textAlign: 'left',
            boxShadow: '0 10px 28px rgba(0, 0, 0, 0.18)'
        },
        contactsPanelHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
            flexWrap: 'wrap'
        },
        contactsPanelTitle: {
            ...withPixelLineHeight(theme.typography.bodyShortBoldLarge),
            color: theme.palette.text01
        },
        contactsPanelBody: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
            marginTop: theme.spacing(2)
        },
        contactsPanelHint: {
            ...withPixelLineHeight(theme.typography.labelRegular),
            color: theme.palette.text03,
            maxWidth: '360px'
        },
        contactsList: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1)
        },
        contactRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1),
            padding: '4px 12px',
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.action02
        },
        contactMeta: {
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
        },
        contactName: {
            ...withPixelLineHeight(theme.typography.bodyShortBold),
            color: theme.palette.text01
        },
        contactPhone: {
            ...withPixelLineHeight(theme.typography.labelRegular),
            color: theme.palette.text03
        },
        contactAction: {
            flexShrink: 0
        },
        inviteButtonRow: {
            display: 'flex',
            gap: theme.spacing(1),
            flexShrink: 0,
            '@media (max-width: 420px)': {
                width: '100%'
            }
        },
        inviteActionButton: {
            minHeight: 36,
            whiteSpace: 'nowrap',
            '@media (max-width: 420px)': {
                flex: 1
            }
        }
    };
});
const Prejoin = ({ deviceStatusVisible, hasJoinByPhoneButton, isDisplayNameVisible, joinConference, joinConferenceWithoutAudio, joiningInProgress, name, participantId, prejoinConfig, readOnlyName, setJoinByPhoneDialogVisiblity, showCameraPreview, showDialog, showErrorOnJoin, showRecordingWarning, showUnsafeRoomWarning, unsafeRoomConsent, updateSettings: dispatchUpdateSettings, videoTrack }) => {
    const showDisplayNameField = useMemo(() => isDisplayNameVisible && !readOnlyName, [isDisplayNameVisible, readOnlyName]);
    const showErrorOnField = useMemo(() => showDisplayNameField && showErrorOnJoin, [showDisplayNameField, showErrorOnJoin]);
    const [showJoinByPhoneButtons, setShowJoinByPhoneButtons] = useState(false);
    // The switch sends interpreter_status; it starts from the availability this
    // client last asked the queue for (unknown = active, the sign-in default).
    const [isInterpreterActive, setIsInterpreterActive] = useState(() => queueService.getLastInterpreterStatus() !== 'inactive');
    // New state for interpreter request management
    const [currentRequest, setCurrentRequest] = useState(null);
    const [minimizedRequests, setMinimizedRequests] = useState([]);
    const [requestAction, setRequestAction] = useState(null);
    const [requestActionError, setRequestActionError] = useState(null);
    const currentRequestRef = useRef(null);
    const requestActionRef = useRef(null);
    const [favouriteContacts, setFavouriteContacts] = useState([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [contactsError, setContactsError] = useState('');
    const [copiedInviteLabel, setCopiedInviteLabel] = useState(null);
    const [showFullContacts, setShowFullContacts] = useState(false);
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const currentRoomName = getCurrentRoomName();
    const inviteLink = currentRoomName
        ? `${window.location.origin}/${currentRoomName}?role=client`
        : '';
    useEffect(() => {
        if (!isClient() || readOnlyName || name) {
            return;
        }
        const preferredName = getFirstName(getPreferredClientName());
        if (preferredName) {
            dispatchUpdateSettings({
                displayName: preferredName
            });
        }
    }, [dispatchUpdateSettings, name, readOnlyName]);
    useEffect(() => {
        const auth = getPersistentJson('vrs_auth_token');
        if (!isClient() || !auth?.token || !currentRoomName) {
            return;
        }
        setContactsLoading(true);
        setContactsError('');
        fetch(`${getApiBase()}/api/contacts`, {
            headers: {
                Authorization: `Bearer ${auth.token}`
            }
        })
            .then(async (response) => {
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload.error || 'Couldn\'t load contacts. Try again.');
            }
            const contacts = Array.isArray(payload.contacts) ? payload.contacts : [];
            setFavouriteContacts(contacts
                .filter(contact => contact?.is_favorite === true && !String(contact.id).startsWith('platform-'))
                .slice(0, 5));
        })
            .catch(error => {
            setContactsError(error instanceof Error ? error.message : 'Couldn\'t load contacts. Try again.');
        })
            .finally(() => {
            setContactsLoading(false);
        });
    }, [currentRoomName]);
    // This prejoin popup owns presentation only. Queue middleware owns room
    // navigation; server acknowledgments and withdrawals own card removal.
    useEffect(() => {
        const clearRequestById = (requestId) => {
            if (!requestId) {
                return;
            }
            if (currentRequestRef.current?.id === requestId) {
                currentRequestRef.current = null;
                setCurrentRequest(null);
            }
            setMinimizedRequests(previous => previous.filter(request => request.id !== requestId));
            if (requestActionRef.current?.requestId === requestId) {
                requestActionRef.current = null;
                setRequestAction(null);
            }
            setRequestActionError(previous => previous?.requestId === requestId ? null : previous);
        };
        const handleInterpreterRequest = (data) => {
            const request = {
                ...data,
                timestamp: data.timestamp || Date.now()
            };
            const previous = currentRequestRef.current;
            if (request.offerId && requestActionRef.current
                && requestActionRef.current.offerId !== request.offerId) {
                requestActionRef.current = null;
                setRequestAction(null);
            }
            if (previous && previous.id !== request.id && !request.offerId) {
                setMinimizedRequests(items => items.some(item => item.id === previous.id)
                    ? items
                    : [...items, previous]);
            }
            if (request.offerId) {
                setMinimizedRequests(items => items.filter(item => !item.offerId));
            }
            currentRequestRef.current = request;
            setCurrentRequest(request);
            setRequestActionError(error => error?.requestId === request.id ? null : error);
        };
        const handleRequestAccepted = (data) => clearRequestById(data.requestId);
        const handleRequestDeclined = (data) => clearRequestById(data.requestId);
        const handleRequestCancelled = (data) => clearRequestById(data.requestId);
        const handleError = (data) => {
            const action = requestActionRef.current;
            if (!action || data.operation !== `${action.kind}_request`
                || data.requestId !== action.requestId) {
                return;
            }
            requestActionRef.current = null;
            setRequestAction(null);
            setRequestActionError({
                message: data.message || 'Not confirmed. Try again.',
                requestId: action.requestId
            });
        };
        queueService.on('interpreterRequest', handleInterpreterRequest);
        queueService.on('requestAccepted', handleRequestAccepted);
        queueService.on('requestDeclined', handleRequestDeclined);
        queueService.on('requestCancelled', handleRequestCancelled);
        queueService.on('error', handleError);
        return () => {
            queueService.off('interpreterRequest', handleInterpreterRequest);
            queueService.off('requestAccepted', handleRequestAccepted);
            queueService.off('requestDeclined', handleRequestDeclined);
            queueService.off('requestCancelled', handleRequestCancelled);
            queueService.off('error', handleError);
        };
    }, []);
    useEffect(() => {
        if (!requestAction) {
            return undefined;
        }
        const timer = window.setTimeout(() => {
            if (requestActionRef.current?.requestId !== requestAction.requestId
                || requestActionRef.current.kind !== requestAction.kind
                || requestActionRef.current.offerId !== requestAction.offerId) {
                return;
            }
            requestActionRef.current = null;
            setRequestAction(null);
            setRequestActionError({
                message: 'Not confirmed. Try again.',
                requestId: requestAction.requestId
            });
        }, 8000);
        return () => window.clearTimeout(timer);
    }, [requestAction]);
    /**
     * Handler for the join button.
     *
     * @param {Object} e - The synthetic event.
     * @returns {void}
     */
    const onJoinButtonClick = () => {
        if (showErrorOnJoin) {
            dispatch(openDisplayNamePrompt({
                onPostSubmit: joinConference,
                validateInput: hasDisplayName
            }));
            return;
        }
        joinConference();
    };
    /**
     * Closes the dropdown.
     *
     * @returns {void}
     */
    const onDropdownClose = () => {
        setShowJoinByPhoneButtons(false);
    };
    /**
     * Displays the join by phone buttons dropdown.
     *
     * @param {Object} e - The synthetic event.
     * @returns {void}
     */
    const onOptionsClick = (e) => {
        e?.stopPropagation();
        setShowJoinByPhoneButtons(show => !show);
    };
    /**
     * Sets the guest participant name.
     *
     * @param {string} displayName - Participant name.
     * @returns {void}
     */
    const setName = (displayName) => {
        dispatchUpdateSettings({
            displayName
        });
    };
    /**
     * Closes the join by phone dialog.
     *
     * @returns {undefined}
     */
    const closeDialog = () => {
        setJoinByPhoneDialogVisiblity(false);
    };
    /**
     * Displays the dialog for joining a meeting by phone.
     *
     * @returns {undefined}
     */
    const doShowDialog = () => {
        setJoinByPhoneDialogVisiblity(true);
        onDropdownClose();
    };
    /**
     * KeyPress handler for accessibility.
     *
     * @param {Object} e - The key event to handle.
     *
     * @returns {void}
     */
    const showDialogKeyPress = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            doShowDialog();
        }
    };
    /**
     * Handler for the interpreter active/inactive toggle.
     *
     * @returns {void}
     */
    const handleInterpreterToggle = (newActiveState = false) => {
        setIsInterpreterActive(newActiveState);
        console.log(`Interpreter ${newActiveState ? 'activated' : 'deactivated'} in queue`);
        if (newActiveState) {
            void prepareInterpreterOfferAlerts();
        }
        // Update status in queue service. The language list must include the
        // language clients request (ASL) or the queue never matches this
        // interpreter — see interpreterLanguages.ts.
        queueService.updateInterpreterStatus(newActiveState ? 'active' : 'inactive', name || 'Anonymous Interpreter', getInterpreterProfileLanguages());
    };
    // Handlers for interpreter popup actions. A click starts a pending
    // operation; only the server acknowledgment/withdrawal listener above
    // removes the card.
    const beginRequestAction = (requestId, kind) => {
        const request = resolveQueueRequest(requestId, currentRequest, minimizedRequests);
        if (!request || requestActionRef.current || offerTiming(request).expired) {
            return;
        }
        requestActionRef.current = { kind, requestId, offerId: request.offerId };
        setRequestActionError(null);
        setRequestAction(requestActionRef.current);
        if (kind === 'accept') {
            queueService.acceptRequest(requestId, request.roomName, request.offerId);
        }
        else {
            queueService.declineRequest(requestId, request.offerId);
        }
    };
    const handleAcceptRequest = (requestId) => beginRequestAction(requestId, 'accept');
    const handleDeclineRequest = (requestId) => beginRequestAction(requestId, 'decline');
    const handleDismissRequest = (requestId) => {
        if (requestActionRef.current?.requestId === requestId) {
            return;
        }
        // Move the popup request to the minimized list without declining it.
        const request = resolveQueueRequest(requestId, currentRequest, minimizedRequests);
        if (currentRequestRef.current?.id === requestId) {
            currentRequestRef.current = null;
            setCurrentRequest(null);
        }
        if (request) {
            setMinimizedRequests(previous => previous.some(item => item.id === requestId) ? previous : [...previous, request]);
        }
    };
    const handleRemoveMinimizedRequest = (requestId) => {
        if (requestActionRef.current?.requestId !== requestId) {
            setMinimizedRequests(previous => previous.filter(request => request.id !== requestId));
            setRequestActionError(previous => previous?.requestId === requestId ? null : previous);
        }
    };
    const copyInviteLink = async (label) => {
        if (!inviteLink) {
            return;
        }
        const inviteText = `Join my secure video room: ${inviteLink}`;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(inviteText);
            }
            else {
                throw new Error('Clipboard unavailable');
            }
            setCopiedInviteLabel(label);
            window.setTimeout(() => setCopiedInviteLabel(current => current === label ? null : current), 2000);
        }
        catch {
            window.prompt('Copy this secure invite link:', inviteLink);
        }
    };
    /**
     * KeyPress handler for accessibility.
     *
     * @param {Object} e - The key event to handle.
     *
     * @returns {void}
     */
    const onJoinConferenceWithoutAudioKeyPress = (e) => {
        if (joinConferenceWithoutAudio
            && (e.key === ' '
                || e.key === 'Enter')) {
            e.preventDefault();
            joinConferenceWithoutAudio();
        }
    };
    /**
     * Gets the list of extra join buttons.
     *
     * @returns {Object} - The list of extra buttons.
     */
    const getExtraJoinButtons = () => {
        const noAudio = {
            key: 'no-audio',
            testId: 'prejoin.joinWithoutAudio',
            icon: IconVolumeOff,
            label: t('prejoin.joinWithoutAudio'),
            onClick: joinConferenceWithoutAudio,
            onKeyPress: onJoinConferenceWithoutAudioKeyPress
        };
        const byPhone = {
            key: 'by-phone',
            testId: 'prejoin.joinByPhone',
            icon: IconPhoneRinging,
            label: t('prejoin.joinAudioByPhone'),
            onClick: doShowDialog,
            onKeyPress: showDialogKeyPress
        };
        return {
            noAudio,
            byPhone
        };
    };
    /**
     * Handle keypress on input.
     *
     * @param {KeyboardEvent} e - Keyboard event.
     * @returns {void}
     */
    const onInputKeyPress = (e) => {
        if (e.key === 'Enter') {
            joinConference();
        }
    };
    const extraJoinButtons = getExtraJoinButtons();
    let extraButtonsToRender = Object.values(extraJoinButtons).filter((val) => !(prejoinConfig?.hideExtraJoinButtons || []).includes(val.key));
    if (!hasJoinByPhoneButton) {
        extraButtonsToRender = extraButtonsToRender.filter((btn) => btn.key !== 'by-phone');
    }
    const hasExtraJoinButtons = Boolean(extraButtonsToRender.length);
    return (React.createElement(PreMeetingScreen, { showDeviceStatus: deviceStatusVisible, showRecordingWarning: showRecordingWarning, showUnsafeRoomWarning: showUnsafeRoomWarning, title: t('prejoin.joinTitle'), videoMuted: !showCameraPreview, videoTrack: videoTrack },
        React.createElement("div", { className: classes.inputContainer, "data-testid": 'prejoin.screen' },
            showDisplayNameField ? (React.createElement(React.Fragment, null,
                React.createElement(Input, { accessibilityLabel: t('dialog.enterDisplayName'), autoComplete: 'name', autoFocus: true, className: classes.input, error: showErrorOnField, id: 'premeeting-name-input', onChange: setName, onKeyPress: showUnsafeRoomWarning && !unsafeRoomConsent ? undefined : onInputKeyPress, placeholder: t('dialog.enterDisplayName'), readOnly: readOnlyName, value: name }))) : (React.createElement("div", { className: classes.avatarContainer },
                React.createElement(Avatar, { className: classes.avatar, displayName: name, participantId: participantId, size: 72 }),
                isDisplayNameVisible && React.createElement("div", { className: classes.avatarName }, name))),
            showErrorOnField && React.createElement("div", { className: classes.error, "data-testid": 'prejoin.errorMessage' }, t('prejoin.errorMissingName')),
            React.createElement("div", { className: classes.dropdownContainer },
                React.createElement(Popover, { content: hasExtraJoinButtons && React.createElement("div", { className: classes.dropdownButtons }, extraButtonsToRender.map(({ key, ...rest }) => (React.createElement(Button, { disabled: joiningInProgress || showErrorOnField, fullWidth: true, key: key, type: BUTTON_TYPES.SECONDARY, ...rest })))), onPopoverClose: onDropdownClose, position: 'bottom', trigger: 'click', visible: showJoinByPhoneButtons },
                    React.createElement(ActionButton, { OptionsIcon: showJoinByPhoneButtons ? IconArrowUp : IconArrowDown, ariaDropDownLabel: t('prejoin.joinWithoutAudio'), ariaLabel: t('prejoin.joinCall'), ariaPressed: showJoinByPhoneButtons, disabled: joiningInProgress
                            || (showUnsafeRoomWarning && !unsafeRoomConsent)
                            || showErrorOnField, hasOptions: hasExtraJoinButtons, onClick: onJoinButtonClick, onOptionsClick: onOptionsClick, role: 'button', tabIndex: 0, testId: 'prejoin.joinMeeting', type: 'primary' }, t('prejoin.joinCall')))),
            isClient() && currentRoomName && (React.createElement("div", { "aria-label": 'Contacts & invite', className: classes.contactsPanel },
                React.createElement("div", { className: classes.contactsPanelHeader },
                    React.createElement("div", null,
                        React.createElement("div", { className: classes.contactsPanelTitle }, "Contacts & Invite")),
                    React.createElement("div", { className: classes.inviteButtonRow },
                        React.createElement(Button, { className: classes.inviteActionButton, icon: IconCopy, label: copiedInviteLabel === 'general' ? 'Copied' : 'Copy Link', onClick: () => copyInviteLink('general'), size: 'small', type: BUTTON_TYPES.SECONDARY }),
                        React.createElement(Button, { className: classes.inviteActionButton, icon: IconUsers, label: showFullContacts ? 'Hide' : 'Contacts', onClick: () => setShowFullContacts(open => !open), size: 'small', type: showFullContacts ? BUTTON_TYPES.PRIMARY : BUTTON_TYPES.SECONDARY }))),
                !showFullContacts && (React.createElement("div", { className: classes.contactsPanelBody },
                    contactsLoading && (React.createElement("div", { className: classes.contactsPanelHint }, "Loading favorite contacts...")),
                    !contactsLoading && contactsError && (React.createElement("div", { className: classes.contactsPanelHint }, contactsError)),
                    !contactsLoading && !contactsError && favouriteContacts.length > 0 && (React.createElement("div", { className: classes.contactsList }, favouriteContacts.map(entry => (React.createElement("div", { className: classes.contactRow, key: String(entry.id) },
                        React.createElement("div", { className: classes.contactMeta },
                            React.createElement("span", { className: classes.contactName }, entry.name),
                            React.createElement("span", { className: classes.contactPhone }, entry.phone_number || entry.email || '')),
                        React.createElement("div", { className: classes.contactAction },
                            React.createElement(Button, { label: copiedInviteLabel === String(entry.id) ? 'Copied' : 'Copy invite', onClick: () => copyInviteLink(String(entry.id)), type: BUTTON_TYPES.SECONDARY }))))))),
                    !contactsLoading && !contactsError && !favouriteContacts.length && (React.createElement("div", { className: classes.contactsPanelHint }, "No favorites")))),
                showFullContacts && (React.createElement("div", { style: { marginTop: '8px' } },
                    React.createElement(ContactsDrawer, { onInviteContact: (contact) => {
                            copyInviteLink(String(contact.id));
                        }, showInviteActions: true }))))),
            isInterpreter() && (React.createElement("div", { style: { marginTop: '16px' } },
                React.createElement("div", { style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255,255,255,0.05)'
                    } },
                    React.createElement("div", { style: { fontSize: '14px', fontWeight: 600 } }, "Queue Status"),
                    React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                        React.createElement(Switch, { checked: isInterpreterActive, onChange: handleInterpreterToggle }),
                        React.createElement("span", { style: {
                                fontSize: '14px',
                                color: isInterpreterActive ? '#00b25d' : '#f44336'
                            } }, isInterpreterActive ? 'Active' : 'Inactive')))))),
        showDialog && (React.createElement(JoinByPhoneDialog, { joinConferenceWithoutAudio: joinConferenceWithoutAudio, onClose: closeDialog })),
        isInterpreter() && currentRequest && (React.createElement(InterpreterRequestPopup, { actionsBlocked: Boolean(requestAction), actionError: requestActionError?.requestId === currentRequest.id
                ? requestActionError.message : undefined, actionPending: requestAction?.requestId === currentRequest.id
                ? requestAction.kind : null, request: currentRequest, onAccept: handleAcceptRequest, onDecline: handleDeclineRequest, onDismiss: handleDismissRequest })),
        isInterpreter() && minimizedRequests.length > 0 && (React.createElement(MinimizedRequestList, { actionError: requestActionError, actionPending: requestAction, requests: minimizedRequests, onAccept: handleAcceptRequest, onDecline: handleDeclineRequest, onRemove: handleRemoveMinimizedRequest }))));
};
/**
 * Maps (parts of) the redux state to the React {@code Component} props.
 *
 * @param {Object} state - The redux state.
 * @returns {Object}
 */
function mapStateToProps(state) {
    const name = getDisplayName(state);
    const showErrorOnJoin = isDisplayNameRequired(state) && !name;
    const { id: participantId } = getLocalParticipant(state) ?? {};
    const { joiningInProgress } = state['features/prejoin'];
    const { room } = state['features/base/conference'];
    const { unsafeRoomConsent } = state['features/base/premeeting'];
    const { showPrejoinWarning: showRecordingWarning } = state['features/base/config'].recordings ?? {};
    return {
        deviceStatusVisible: isDeviceStatusVisible(state),
        hasJoinByPhoneButton: isJoinByPhoneButtonVisible(state),
        isDisplayNameVisible: isPrejoinDisplayNameVisible(state),
        joiningInProgress,
        name,
        participantId,
        prejoinConfig: state['features/base/config'].prejoinConfig,
        readOnlyName: isNameReadOnly(state),
        showCameraPreview: !isVideoMutedByUser(state),
        showDialog: isJoinByPhoneDialogVisible(state),
        showErrorOnJoin,
        showRecordingWarning: Boolean(showRecordingWarning),
        // Flag first: the room-name scorer is a large on-demand download.
        showUnsafeRoomWarning: isUnsafeRoomWarningEnabled(state) && isInsecureRoomName(room),
        unsafeRoomConsent,
        videoTrack: getLocalJitsiVideoTrack(state)
    };
}
const mapDispatchToProps = {
    joinConferenceWithoutAudio: joinConferenceWithoutAudioAction,
    joinConference: joinConferenceAction,
    setJoinByPhoneDialogVisiblity: setJoinByPhoneDialogVisiblityAction,
    updateSettings
};
export default connect(mapStateToProps, mapDispatchToProps)(Prejoin);
